import { MatchEvent } from 'shared/types/match-event';
import { VideoMarkerDraft } from 'modules/video-markers/domain/interfaces';

/** Какую позицию программы пересчитывать: записи (файл) или эфира (VOD площадки). */
export type VideoTimeline = 'recording' | 'streaming';

export interface EventVideoPosition {
  /** По хронометражу: игровое время события, пересчитанное через опорные точки. */
  byClockMs: number | null;
  /** По получению: позиция программы, когда событие дошло до оверлея. */
  byReceiptMs: number | null;
  /** На сколько статист и доставка отстали от события: `byReceiptMs − byClockMs`. */
  statistDelayMs: number | null;
  /** Какая из двух позиций основная; `null` — ни одной нет. */
  source: 'clock' | 'receipt' | null;
  positionMs: number | null;
  /** Шаг, с которым программа отдала позицию основного источника: 1 у OBS, 1000 у vMix. */
  precisionMs: number | null;
  /**
   * Хронометраж не описывает момент события, и основной тогда — позиция по получению. Два случая:
   * секунда события совпала с секундой, на которой часы стояли (событие случилось где-то в паузе,
   * и хронометраж говорит лишь, когда она началась), или событие пришло, когда часы стояли дольше
   * `LONG_CLOCK_STOP_MS` (игра шла без часов — живая лига 07.10.2026, 4 минуты).
   */
  duringPause: boolean;
}

/**
 * Сколько часы могут стоять, пока хронометраж ещё описывает игру. Дольше — статист забыл их
 * запустить, а события вносит по ходу: секунды таких событий — время остановки, а не момента.
 */
export const LONG_CLOCK_STOP_MS = 60_000;

/**
 * Насколько хронометраж может обогнать позицию между соседними опорами и остаться той же линией.
 * Больше — статист перевёл часы вперёд, и это новый отрезок. Как `stopAfterMs` у `ClockAnchorTracker`.
 */
const FORWARD_JUMP_TOLERANCE_MS = 2_000;

type MarkerRow = Pick<
  VideoMarkerDraft,
  | 'kind'
  | 'eventId'
  | 'matchPeriod'
  | 'matchSecond'
  | 'clockRunning'
  | 'recordingMs'
  | 'streamingMs'
  | 'precisionMs'
  | 'clientReceivedAt'
>;

type PositionOf = (row: MarkerRow) => number | null;

/**
 * Сводит две позиции события в видео — по хронометражу и по получению.
 *
 * `markers` — строки `video_markers` одного матча **и одной программы**: опорные точки
 * (`kind = 'clock'`) и, если есть, метка этого события по получению. Позиция по хронометражу не
 * хранится, а вычисляется здесь, чтобы ошибку формулы можно было исправить задним числом.
 *
 * Опора — точка того же тайма с идущими часами и хронометражем не позже секунды события; к её
 * позиции прибавляется разница секунд. Из подходящих берётся **последняя по времени**: остановка
 * часов и ручная правка секунд начинают новый отрезок, и событие относится к свежему. Точка не
 * может быть позже получения события, а результат — позже позиции по получению (событие не
 * случается после того, как его увидели): такая опора пропускается и пробуется предыдущая.
 *
 * **Пачка после обрыва связи.** Несколько точек с одной позицией и разными секундами — очередь,
 * которую джойстик дослал разом: позиция у них — время доставки, а не тика, и опоздала вся пачка,
 * включая последнюю точку (MKV 08.10.2026: 2422, 2423, 2433 на 28,1 с при верных ~14,4 с). Опорой
 * такие точки не становятся.
 *
 * **Опора после события.** Если до получения опоры нет (например, её съела пачка), берётся первая
 * точка **после** — с идущими часами, хронометражем не раньше секунды события и в том же отрезке,
 * что и получение: между ними нет остановки часов и правки секунд, а секунда события не раньше
 * старта отрезка. Позиция считается от неё назад.
 *
 * `extraMinutes` не нужен: добавленное время уже входит в `seconds` события
 * (`games/domain/utils/match-periods-settings.ts`, `getGameEventTime`).
 */
export function resolveEventVideoPosition(
  event: Pick<MatchEvent, 'id' | 'period' | 'seconds'>,
  markers: readonly MarkerRow[],
  timeline: VideoTimeline,
): EventVideoPosition {
  const positionOf: PositionOf = (row) => (timeline === 'recording' ? row.recordingMs : row.streamingMs);

  const receipt = markers.find((row) => row.kind === 'event' && row.eventId === event.id) ?? null;
  const byReceiptMs = receipt ? positionOf(receipt) : null;
  const receivedAt = receipt ? Date.parse(receipt.clientReceivedAt) : null;
  const notAfterReceipt = (row: MarkerRow) => receivedAt === null || Date.parse(row.clientReceivedAt) <= receivedAt;

  const periodClock = markers
    .filter((row) => row.kind === 'clock' && row.matchPeriod === event.period && row.matchSecond !== null)
    .sort((a, b) => Date.parse(a.clientReceivedAt) - Date.parse(b.clientReceivedAt));
  const burst = findBurstRows(periodClock, positionOf);
  const isAnchorable = (row: MarkerRow) => row.clockRunning === true && positionOf(row) !== null && !burst.has(row);
  /** Позиция события от опоры `row`, если она не позже позиции по получению (с допуском шага). */
  const positionFrom = (row: MarkerRow) => {
    const candidateMs = positionOf(row)! + (event.seconds - row.matchSecond!) * 1000;
    const toleranceMs = Math.max(row.precisionMs, receipt?.precisionMs ?? 0);
    return byReceiptMs !== null && candidateMs > byReceiptMs + toleranceMs ? null : candidateMs;
  };

  const samePeriodClock = periodClock.filter(notAfterReceipt).reverse();

  let anchor: MarkerRow | null = null;
  let byClockMs: number | null = null;
  for (const row of samePeriodClock) {
    if (!isAnchorable(row) || row.matchSecond! > event.seconds) continue;

    const candidateMs = positionFrom(row);
    if (candidateMs === null) continue;

    anchor = row;
    byClockMs = candidateMs;
    break;
  }

  if (anchor === null && receivedAt !== null) {
    const segments = splitIntoSegments(periodClock, positionOf, burst);
    const receiptSegment = segments.segmentAt(receivedAt);
    const eventInSegment = event.seconds >= segments.startSecondOf(receiptSegment);
    for (const row of eventInSegment ? periodClock : []) {
      if (segments.of(row) !== receiptSegment || !isAnchorable(row) || row.matchSecond! < event.seconds) continue;

      const candidateMs = positionFrom(row);
      if (candidateMs === null) continue;

      anchor = row;
      byClockMs = candidateMs;
      break;
    }
  }

  const anchorAt = anchor ? Date.parse(anchor.clientReceivedAt) : null;
  const stoppedOnEventSecond =
    anchorAt !== null &&
    samePeriodClock.some(
      (row) =>
        row.clockRunning === false && row.matchSecond === event.seconds && Date.parse(row.clientReceivedAt) > anchorAt,
    );
  const lastBeforeReceipt = receivedAt === null ? undefined : samePeriodClock.find((row) => !burst.has(row));
  const longStopAtReceipt =
    lastBeforeReceipt !== undefined &&
    lastBeforeReceipt.clockRunning === false &&
    receivedAt! - Date.parse(lastBeforeReceipt.clientReceivedAt) > LONG_CLOCK_STOP_MS;
  const duringPause = stoppedOnEventSecond || longStopAtReceipt;

  const statistDelayMs = byClockMs !== null && byReceiptMs !== null ? byReceiptMs - byClockMs : null;
  const useClock = byClockMs !== null && !(duringPause && byReceiptMs !== null);
  const source = useClock ? 'clock' : byReceiptMs !== null ? 'receipt' : null;

  return {
    byClockMs,
    byReceiptMs,
    statistDelayMs,
    source,
    positionMs: source === 'clock' ? byClockMs : source === 'receipt' ? byReceiptMs : null,
    precisionMs: source === 'clock' ? anchor!.precisionMs : source === 'receipt' ? receipt!.precisionMs : null,
    duringPause,
  };
}

/**
 * Точки пачки: на одной позиции хронометраж прошёл больше, чем позволяет шаг программы. Так
 * выглядит очередь, досланная разом после обрыва связи. У vMix позиция — целые секунды, и две
 * честные точки на соседних секундах могут делить одну позицию; разброс в один шаг поэтому допустим.
 */
function findBurstRows(rows: readonly MarkerRow[], positionOf: PositionOf): Set<MarkerRow> {
  const byPosition = new Map<number, MarkerRow[]>();
  for (const row of rows) {
    const positionMs = positionOf(row);
    if (positionMs !== null) byPosition.set(positionMs, [...(byPosition.get(positionMs) ?? []), row]);
  }

  const burst = new Set<MarkerRow>();
  byPosition.forEach((group) => {
    const seconds = group.map((row) => row.matchSecond!);
    const spreadMs = (Math.max(...seconds) - Math.min(...seconds)) * 1000;
    if (spreadMs > Math.max(...group.map((row) => row.precisionMs))) group.forEach((row) => burst.add(row));
  });
  return burst;
}

/**
 * Делит точки тайма (по времени получения) на отрезки одной линии «секунда ↔ позиция». Отрезок
 * кончается остановкой часов и рвётся правкой секунд — скачком назад или вперёд сильнее хода
 * позиции (после остановки — любым сдвигом секунд: часы продолжают с той, на которой встали).
 * Точки пачки линию не задают, скачком и остановкой не считаются.
 */
function splitIntoSegments(rows: readonly MarkerRow[], positionOf: PositionOf, burst: ReadonlySet<MarkerRow>) {
  const segmentOf = new Map<MarkerRow, number>();
  const segmentAfter: { atMs: number; segment: number }[] = [];
  /** С какой секунды хронометража начался отрезок: раньше неё событие в него не попадает. */
  const startSecond: number[] = [rows[0]?.matchSecond ?? 0];
  let previous: MarkerRow | null = null;
  /** Секунда, на которой часы встали: после остановки они идут дальше с неё же. */
  let stoppedAt: MarkerRow | null = null;
  const openSegment = (fromSecond: number) => startSecond.push(fromSecond);

  for (const row of rows) {
    const positionMs = positionOf(row);
    if (row.clockRunning === true && positionMs !== null && !burst.has(row)) {
      const reference = previous ?? stoppedAt;
      if (reference !== null) {
        const secondsMs = (row.matchSecond! - reference.matchSecond!) * 1000;
        // Пока часы стояли, позиция шла, а секунды — нет: после остановки сравниваем с нулём.
        const elapsedMs = previous !== null ? positionMs - positionOf(previous)! : 0;
        const toleranceMs = FORWARD_JUMP_TOLERANCE_MS + Math.max(row.precisionMs, reference.precisionMs);
        if (secondsMs < 0 || secondsMs - elapsedMs > toleranceMs) openSegment(row.matchSecond!);
      }
      previous = row;
      stoppedAt = null;
    }

    segmentOf.set(row, startSecond.length - 1);
    // «Часы стоят» из пачки — это оверлей не дождался тиков во время обрыва, а не остановка.
    if (row.clockRunning === false && !burst.has(row)) {
      openSegment(row.matchSecond!);
      previous = null;
      stoppedAt = row;
    }
    segmentAfter.push({ atMs: Date.parse(row.clientReceivedAt), segment: startSecond.length - 1 });
  }

  return {
    of: (row: MarkerRow) => segmentOf.get(row),
    /** Отрезок, открытый в момент `atMs`: тот, что остался после последней точки до него. */
    segmentAt: (atMs: number) => segmentAfter.filter((entry) => entry.atMs <= atMs).pop()?.segment ?? 0,
    startSecondOf: (segment: number) => startSecond[segment],
  };
}
